import theme from '../../../../theme';

/**
 * Styles for the Gallery component
 * @module GallerySx
 * @description Contains all style objects for the Gallery component with exact visual matching
 */

/**
 * Paper container styles
 * @type {Object}
 */
export const paperContainer = {
  padding: {
    xs: '1rem 1rem',
    sm: '1.25rem 1.5rem',
    md: '1.5rem 2rem',
  },
  backgroundColor: theme.palette.background.paper,
};

/**
 * Main container styles
 * @type {Object}
 */
export const mainContainere = {
  flex: 1,
  padding: {
    xs: '1rem',
    sm: '1.5rem',
    md: '2rem',
  },
  maxWidth: 'none',
  '@media (min-width: 1200px)': {
    maxWidth: 'none !important',
  },
};

/**
 * Image styles
 * @type {Object}
 */
export const exampleImagesContainer = {
  position: 'absolute',
  right: '0.25rem',
  top: '0.25rem',
  zIndex: '10',
};

/**
 * Subtitle text styles
 * @type {Object}
 */
export const subtitleText = {
  marginTop: 0,
  color: theme.palette.text.primary,
  textAlign: 'center',
};

/**
 * CarFax link styles
 * @type {Object}
 */
export const carFaxLink = {
  color: theme.palette.primary.main,
  textDecoration: 'none',
};

/**
 * Section title styles
 * @type {Object}
 */
export const sectionTitle = {
  color: theme.palette.text.form,
  fontSize: {
    xs: '1rem',
    sm: '1.05rem',
    md: '1.1rem',
  },
  fontWeight: {
    xs: 500,
    sm: 600,
  },
};

/**
 * Video upload container styles
 * @type {Object}
 */
export const videoUploadContainer = {
  display: 'flex',
  flexDirection: {
    xs: 'column',
    sm: 'row',
  },
  alignItems: 'center',
  gap: '1rem',
  border: `0.1rem solid ${theme.palette.border.disabled}`,
  padding: {
    xs: '1rem',
    sm: '0.5rem 0.5rem 0.5rem 1rem',
  },
  borderRadius: '0.5rem',
  maxWidth: {
    xs: '100%',
    sm: '35rem',
  },
};

/**
 * Video upload text styles
 * @type {Object}
 */
export const videoUploadText = {
  flex: 1,
  textAlign: 'center',
};

/**
 * Upload button styles
 * @type {Object}
 */
export const uploadButton = {
  height: '90%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
};

export const individualImageItemWidth = '13.5rem';

/**
 * Image grid container styles
 * @type {Object}
 */
export const imageGridContainer = {
  position: 'relative',
  // width: '100%',
  height: {
    xs: '8rem',
    sm: '8.5rem',
    md: '11rem',
    lg: '12rem',
  },
  border: `0.1rem solid ${theme.palette.border.disabled}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '0.5rem',
  overflow: 'hidden',
};

/**
 * Image grid container styles
 * @type {Object}
 */
export const imageIndividualImageGridContainer = {
  minWidth: "300px !important"
};

export const imageInputContainer = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

/**
 * Image styles
 * @type {Object}
 */
export const imageStyle = {
  objectFit: 'cover',
};

export const imagesGridContainer = {
  // display: 'flex',
  flexWrap: "nowrap",
  overflowY: "scroll",
  alignItems: 'center',
  gap: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
  },
};

/**
 * Add image container styles
 * @type {Object}
 */
export const addImageContainer = {
  width: '10rem',
  height: '8rem',
  border: `0.1rem solid ${theme.palette.border.disabled}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '0.5rem',
  backgroundColor: theme.palette.background.default,
  cursor: 'pointer',
  position: 'relative',
};

/**
 * Hidden file input styles
 * @type {Object}
 */
export const hiddenFileInput = {
  opacity: 0,
  position: 'absolute',
  width: '10rem',
  height: '8rem',
  cursor: 'pointer',
  zIndex: '10',
};

/**
 * Add image content styles
 * @type {Object}
 */
export const addImageContent = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.25rem',
};

/**
 * Add image icon styles
 * @type {Object}
 */
export const addImageIcon = {
  fontSize: '2rem',
  color: theme.palette.text.disabled,
};

/**
 * Add image text styles
 * @type {Object}
 */
export const addImageText = {
  color: theme.palette.text.disabled,
  fontSize: {
    xs: '0.85rem',
    sm: '0.9rem',
    md: '1rem',
  },
  textAlign: 'center',
};

/**
 * Form container styles - Modified to completely disable MUI's responsive max-width
 * @type {Object}
 */
export const formContainer = {
  flex: 1,
  padding: '2rem',
  maxWidth: 'none',
  // Important: Disable all responsive max-width constraints
  '@media (min-width: 0px)': {
    maxWidth: 'none',
  },
  '@media (min-width: 600px)': {
    maxWidth: 'none',
  },
  '@media (min-width: 900px)': {
    maxWidth: 'none',
  },
  '@media (min-width: 1200px)': {
    maxWidth: 'none',
  },
};

/**
 * Content section styles
 * @type {Object}
 */
export const contentSection = {
  marginBottom: '2rem',
};

/**
 * Divider styles
 * @type {Object}
 */
export const dividerStyle = {
  marginBottom: '2rem',
  backgroundColor: theme.palette.border.disabled,
};

/**
 * CarFax upsell full section container
 */
export const carfaxFullSection = {
  width: "100%",
  marginBottom: '2rem',
  padding: '1.5rem',
  border: `1px solid ${theme.palette.border.disabled}`,
  borderRadius: '0.5rem',
  backgroundColor: theme.palette.background.paper,
};

/**
 * CarFax main headline styles
 */
export const carfaxHeadline = {
  fontSize: "1.15rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '0.25rem',
};

/**
 * CarFax bolded description
 */
export const carfaxSubHeadline = {
  color: theme.palette.text.primary,
  fontSize: "1.05rem",
  fontWeight: 500,
  marginBottom: '0.75rem',
};

/**
 * CarFax disclaimer / fine print
 */
export const carfaxDisclaimer = {
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
  lineHeight: 1.6,
  marginBottom: '1.5rem',
  '& span': {
    color: theme.palette.secondary.main,
    fontWeight: 500,
  },
};

/**
 * PayPal buttons layout
 */
export const paypalButtonsGroup = {
  marginTop: "0.5rem",
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
};

/**
 * PayPal styled button placeholder
 */
export const paypalButtonStyle = {
  backgroundColor: '#ffc439',
  padding: '0.5rem 1.25rem',
  borderRadius: '6px',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
  color: '#111',
  fontFamily: `'Poppins', sans-serif`,
};

/**
 * Card styled button
 */
export const cardButtonStyle = {
  border: `2px solid ${theme.palette.primary.main}`,
  padding: '0.5rem 1.25rem',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
  color: theme.palette.primary.main,
  fontFamily: `'Poppins', sans-serif`,
  backgroundColor: 'transparent',
};
